import AddIcon from '@mui/icons-material/Add';
import CloudDownload from '@mui/icons-material/CloudDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import {
	Box,
	Button,
	FormControl,
	FormHelperText,
	TextField,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import React, { useEffect } from 'react';
import {
	Controller,
	UseControllerProps,
	useFieldArray,
	useFormContext,
} from 'react-hook-form';
import { MediaFieldConfiguration } from '../../../api/ConfigApi';
import MediaApi from '../../../api/MediaApi';
import { AuthData } from '../../../context/auth';
import { MediaResponse } from '../../../types/media';
import FieldLabel from '../../fields/FieldLabel';
import { MEDIA_FIELD } from '../data/constants';

interface Props extends UseControllerProps, MediaFieldConfiguration {
	authData: AuthData;
}

const MediaField = (props: Props) => {
	const { setValue } = useFormContext();
	const { authorizationToken } = props.authData.auth;
	const { control, name, title, optional, repeatable, authData, disabled } =
		props;
	const { hasPermission } = authData;
	const { fields, insert, remove, update } = useFieldArray({
		control,
		name: name,
	});

	useEffect(() => {
		if (fields.length === 0 && !optional) {
			setValue(`${name}`, [{ locator: '' }], { shouldDirty: false });
		}
	});

	const [loading, setLoading] = React.useState<boolean[]>(
		fields.map(() => false),
	);
	// Store all media file details here in local state--only locator will be stored in the form data
	const buildMediaDetailsState = (configFields: Record<'id', string>[]) =>
		configFields.map((field: any) => {
			return {
				id: field.id,
				locator: field.locator,
				url: '',
				mimeType: '',
				expiresTimestamp: '',
				fileName: '',
				contentLength: '',
			};
		});
	const [mediaDetails, setMediaDetails] = React.useState<MediaResponse[]>(
		buildMediaDetailsState(fields),
	);
	const labelRef = React.useRef<HTMLLabelElement>(null);

	const handleClick = () => {
		labelRef.current.click();
	};

	// Get the initial files from the media locators
	// This is sometimes included with characteristics, sometimes not (endorsements and renewals)
	// So I guess we'll just always grab them here
	React.useEffect(() => {
		let mounted = true;
		// since we have an internal state of `mediaDetails` based on props `fields`
		// we need to keep them in sync whenever props `fields` change.
		// in order to keep them in sync, updated version of `mediaDetails` is stored in `upcomingMediaDetails`
		// then `upcomingMediaDetails` will be used to fetch file details and will be used to set the upcoming state of `mediaDetails`
		const getFileDetails = async () => {
			const upcomingMediaDetails = buildMediaDetailsState(fields);
			const promises = (fields as any).map(
				async (field: MediaResponse, index: number) => {
					return upcomingMediaDetails[index]?.fileName === '' &&
						field.locator !== ''
						? MediaApi.getFile(authorizationToken, field.locator)
						: Promise.resolve(null);
				},
			);
			const files = await Promise.all(promises);
			const newDetails: MediaResponse[] = [...upcomingMediaDetails];

			files.forEach((file, index) => {
				if (
					file &&
					file.data.fileName !== upcomingMediaDetails[index]?.fileName
				) {
					// update mediaDetails at index as opposed to using update to 'dirty' the field upon fetching
					newDetails[index] = file.data;
				}
			});
			setMediaDetails(newDetails);
		};

		if (mounted && hasPermission('media.fetch')) {
			getFileDetails();
		}

		return () => {
			mounted = false;
		};
	}, [authorizationToken, fields, hasPermission, update]);

	const updateMediaDetails = (update: MediaResponse, index: number) => {
		const newMediaDetails = [...mediaDetails];
		newMediaDetails[index] = update;
		setMediaDetails(newMediaDetails);
	};

	const removeMediaDetails = (index: number) => {
		const newMediaDetails = [...mediaDetails];
		newMediaDetails.splice(index, 1);
		setMediaDetails(newMediaDetails);
	};

	const insertMediaDetailsAtIndex = (index: number) => {
		const newMediaDetails = [...mediaDetails];
		newMediaDetails.splice(index, 0, {
			id: '',
			locator: '',
			url: '',
			mimeType: '',
			expiresTimestamp: '',
			fileName: '',
			contentLength: '',
		});
		setMediaDetails(newMediaDetails);
	};

	const uploadFile = async (file: File, index: number) => {
		try {
			const newLoading = [...loading];
			newLoading[index] = true;
			setLoading(newLoading);
			const res = await MediaApi.uploadFile(
				authData.auth.authorizationToken,
				file,
			);
			const addedFileLocator = res.data.locator;
			// update field's form data with new locator
			update(index, {
				locator: addedFileLocator,
			});
			// update media details at index
			updateMediaDetails(res.data, index);
			setLoading([...loading.slice(0, index), false]);
		} catch (error) {
			console.error(error);
		}
	};

	const renderUploadButton = () => (
		<Box>
			{fields.length === 0 ? (
				<Button
					disabled={disabled || !hasPermission('media.upload')}
					variant='contained'
					color='primary'
					sx={{
						backgroundColor: '#02457A',
						color: '#FFFFFF',
						margin: '5px',
						height: '32px',
					}}
					component='span'
					startIcon={<CloudUploadIcon style={{ paddingRight: '3px' }} />}
					onClick={() => {
						insert(0, {
							locator: '',
						});
						setMediaDetails((mediaDetails) => [
							...mediaDetails,
							{
								id: '',
								locator: '',
								url: '',
								mimeType: '',
								expiresTimestamp: '',
								fileName: '',
								contentLength: '',
							},
						]);
					}}
					aria-label={MEDIA_FIELD.ARIA_LABEL.UPLOAD}
				>
					{MEDIA_FIELD.UPLOAD}
				</Button>
			) : null}
		</Box>
	);

	const renderFiles = () => {
		return fields.map((field, index) => {
			if (!hasPermission('media.fetch')) {
				return (
					<TextField
						key={field.id}
						disabled={true}
						value={MEDIA_FIELD.NO_PERMISSION}
					/>
				);
			}
			return (
				<React.Fragment key={field.id}>
					<Controller
						control={control}
						name={`${name}.${index}.locator`}
						render={({
							field: { onChange, value, ref },
							fieldState: { error },
						}) => (
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									mt: index > 0 ? 2 : 0,
								}}
							>
								<FormControl fullWidth error={!!error} disabled={disabled}>
									<Input
										value={mediaDetails[index]?.fileName || ''}
										error={!!error}
										fullWidth
										disabled
										required={!optional}
										endAdornment={
											<InputAdornment position='end'>
												{hasPermission('media.upload') &&
												!mediaDetails[index]?.url ? (
													<IconButton
														aria-label={MEDIA_FIELD.ARIA_LABEL.UPLOAD}
														component='label'
														disabled={disabled || loading.includes(true)}
														htmlFor='config-file-upload'
														ref={labelRef}
														onClick={(e: any) => {
															if (e.type.includes('key')) {
																handleClick();
															}
														}}
													>
														<CloudUploadIcon />
														<input
															disabled={disabled}
															onChange={(e) =>
																uploadFile(e.target.files[0], index)
															}
															checked={value}
															ref={ref}
															type='file'
															id='config-file-upload'
															hidden
														/>
													</IconButton>
												) : (
													mediaDetails[index]?.url && (
														<IconButton
															aria-label={MEDIA_FIELD.ARIA_LABEL.DOWNLOAD}
															href={mediaDetails[index]?.url}
														>
															<CloudDownload />
														</IconButton>
													)
												)}
												<IconButton
													disabled={disabled}
													aria-label={MEDIA_FIELD.ARIA_LABEL.REMOVE}
													component='label'
													onClick={() => {
														remove(index);
														// remove media details at index
														removeMediaDetails(index);
													}}
												>
													<DeleteIcon />
												</IconButton>
												{loading[index] && <CircularProgress size={20} />}
											</InputAdornment>
										}
									/>
									<FormHelperText id='file-upload-helper-text'>
										{!!error ? error?.message : ''}
									</FormHelperText>
								</FormControl>
								{repeatable && (
									<IconButton
										disabled={disabled}
										sx={{ ml: 1, width: 24, height: 24 }}
										type='button'
										onClick={() => {
											insert(index + 1, {
												locator: '',
											});
											// insert media details at index
											insertMediaDetailsAtIndex(index + 1);
										}}
										aria-label='add'
									>
										<AddIcon />
									</IconButton>
								)}
							</Box>
						)}
					/>
				</React.Fragment>
			);
		});
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<FieldLabel title={title} required={!optional} />
			{renderUploadButton()}
			{renderFiles()}
		</Box>
	);
};

export default MediaField;
